import { useEffect } from 'react';
import type { FC } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import { useInView } from 'react-intersection-observer';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import CapsuleAccordion from 'src/app/components/Elements/Accordion';
import SlideInModal from 'src/app/components/Elements/SlideInModal';
import { Typography } from 'src/app/components/Elements/Typography';
import { segmentTrack, setShowFaqModal } from 'src/app/state/actions';
import { getCurrentPathname, getCurrentPathParams, shouldShowFaqModal } from 'src/app/state/selectors';
import { useDispatch, useSelector } from 'src/app/state/store';
import type { RenderedAccordionQuestions } from 'src/app/types';
import type { ThemeSpaceType } from 'src/styles/theme';
import { theme } from 'src/styles/theme';

import { AccordionContainer } from './styles';
import styles from './styles.module.scss';

interface QuestionProps {
  answer: string | JSX.Element;
  question: string | JSX.Element;
}

interface FaqModalProps {
  body?: string;
  contentMargin?: ThemeSpaceType;
  questions: QuestionProps[];
  title: string;
  expandFAQEvent?: UnifiedEvent;
  /**
   * hasViewFAQEvent boolean, defaults to true to fire view faq modal event
   */
  hasViewFAQEvent?: boolean;
  expandAdditionalProperties?: { [key: string]: string | undefined };
}

export const FaqModal: FC<FaqModalProps> = ({
  body,
  contentMargin,
  questions,
  title,
  expandFAQEvent,
  hasViewFAQEvent = true,
  expandAdditionalProperties = {},
}) => {
  const pathname = useSelector(getCurrentPathname);
  const pathparams = useSelector(getCurrentPathParams);
  const showFaqModal = useSelector(shouldShowFaqModal);
  const dispatch = useDispatch();

  const { inView, ref } = useInView({ threshold: 0.25 });

  useEffect(() => {
    if (inView && hasViewFAQEvent) {
      dispatch(segmentTrack(UNIFIED.viewFaqModal, { pagepath: `${pathname}${pathparams}` }));
    }
  }, [dispatch, inView, pathname, pathparams]);

  useEffect(() => {
    const bodyEl = document.querySelector('body');

    if (showFaqModal) {
      bodyEl?.classList.add(styles.noScroll);
    } else {
      bodyEl?.classList.remove(styles.noScroll);
    }
  }, [showFaqModal]);

  const handleCloseModal = () => {
    dispatch(setShowFaqModal(false));
  };

  const handleExpand = (questions: RenderedAccordionQuestions, expandedIndex: number[]) => {
    const text = questions[expandedIndex[0]]?.question;
    if (text) {
      const eventName = expandFAQEvent ?? UNIFIED.expandFaqModal;
      dispatch(segmentTrack(eventName, { text, ...expandAdditionalProperties }));
    }
  };

  const isMobile = useMediaQuery(theme.breakpoints.mediumDown);

  const slideDirection = isMobile ? 'up' : 'right';

  return (
    <SlideInModal
      contentMargin={contentMargin}
      direction={slideDirection}
      isShown={showFaqModal}
      modalContainerClassName={styles.modalContainer}
      onClose={handleCloseModal}
    >
      <div className={styles.scroll} ref={ref}>
        <Typography variant={isMobile ? 'h3' : 'h2'} marginBottom={body ? 's2' : 'none'}>
          {title}
        </Typography>
        {body && <Typography variant="body">{body}</Typography>}
        <AccordionContainer>
          <CapsuleAccordion
            bodyClassName={styles.body}
            expandCallback={handleExpand}
            headingClassName={styles.heading}
            hideOnlyFirstLine
            questions={questions}
            sectionHeaderLevel={4}
            titleClassName={styles.title}
            useV2Styles
            wrapperClassName={styles.wrapper}
          />
        </AccordionContainer>
      </div>
    </SlideInModal>
  );
};
